import SortByIdDropdown from 'app/components/SortByIdDropdown';
import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import useForm from 'assets/components/form/hooks/Form';
import RenderProps from 'assets/components/helpers/RenderProps';
import Icon, { iconStyles } from 'assets/components/icon/Icon';
import SearchInput from 'assets/components/inputs/search/SearchInput';
import FilterWrapper from 'assets/components/inputs/wrappers/FilterWrapper';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeWrapperGrid from 'assets/themes/wrappers/Theme.Wrapper.Grid';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { compact, isNull, omitBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { isDesktop } from 'react-device-detect';

export default function FilterForm<T>(props: Template.FilterForm.Import<T>) {
  const { lang } = getLocales();
  const filterForm = useForm<T>({ default: props.filters ?? {} });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    filterForm.reload();
  }, [props.filters]);

  const cleanValues = useMemo(() => omitBy(filterForm.data, isNull) as T, [filterForm.data]);
  useEffect(() => {
    if (isDesktop && props.onOpen) {
      props.onOpen();
    }
  }, []);

  const renderedFilters = RenderProps(props, 'render', { tempFilters: filterForm.data, setFilters: filterForm.update });
  if (isDesktop) {
    let inputArray = [];
    if (Array.isArray(renderedFilters)) {
      inputArray = compact(renderedFilters);
    } else if (
      renderedFilters &&
      typeof renderedFilters === 'object' &&
      renderedFilters.type === React.Fragment &&
      Array.isArray(renderedFilters.props.children)
    ) {
      inputArray = compact(renderedFilters.props.children);
    } else {
      inputArray = compact([renderedFilters]);
    }
    const originalInputLength = inputArray.length;
    if (!isExpanded) {
      inputArray = inputArray.slice(0, 6);
    }
    return (
      <>
        <Box class={ThemeWrapperStack}>
          <h3 style={{ marginRight: 'auto' }}>{lang.searchBy}</h3>
          <Box class={ThemeWrapperStack}>
            {props.onSearch && (
              <SearchInput
                value={props.search}
                onSearch={props.onSearch}
                threshold={2}
                placeholder={`${lang.search}...`}
              />
            )}
            {props.onSortById && <SortByIdDropdown onSort={props.onSortById} />}
            {props.headerActions}
            {originalInputLength > 6 && (
              <Button
                class={ThemeButtonCircle}
                active
                media={<Icon class={!isExpanded ? 'fas-caret-down' : 'fas-caret-up'} />}
                tabIndex={1}
                onClick={setIsExpanded}
                data={!isExpanded}
              />
            )}
          </Box>
        </Box>
        <Box class={ThemeWrapperGrid}>
          {inputArray}
          <Box
            class={ThemeWrapperStack}
            style={{
              gridColumn: '-3 / -1',
              justifyContent: 'flex-end',
              marginTop: 'auto',
            }}
          >
            {props.actions}
            {props.onReset && inputArray.length > 0 && (
              <Button
                {...(props.subcomponents && props.subcomponents.submitButton)}
                media={<Icon class={iconStyles.common.restore} />}
                render={lang.reset}
                tabIndex={1}
                onClick={props.onReset}
              />
            )}
            {inputArray.length > 0 && (
              <Button
                {...(props.subcomponents && props.subcomponents.resetButton)}
                media={<Icon class={iconStyles.common.reload} />}
                render={lang.apply}
                disabled={!filterForm.isUpdated}
                active={filterForm.isUpdated}
                tabIndex={1}
                data={cleanValues}
                onClick={props.onFilter}
              />
            )}
          </Box>
        </Box>
      </>
    );
  }
  return (
    <>
      {props.onSearch && (
        <SearchInput value={props.search} onSearch={props.onSearch} threshold={2} placeholder={`${lang.search}...`} />
      )}
      {props.onSortById && <SortByIdDropdown onSort={props.onSortById} />}
      {props.headerActions}
      {props.actions}
      <DropdownButton
        subcomponents={props.subcomponents && props.subcomponents.dropdownButton}
        onAnimateIn={props.onOpen}
        htmlElementProps={{ title: lang.filters }}
        horizontalPosition="right"
        tabIndex={null}
        buttonMedia={<Icon class={iconStyles.common.filters} />}
        dropdownActions={(anim) => (
          <>
            <Button
              {...(props.subcomponents && props.subcomponents.cancelButton)}
              media={<Icon class={iconStyles.common.right} />}
              tabIndex={1}
              onClick={anim.animateOut}
              htmlElementProps={{ title: lang.close }}
            />
          </>
        )}
        title={lang.filters}
        dropdownOptions={() => (
          <>
            <FilterWrapper>{renderedFilters}</FilterWrapper>
          </>
        )}
        dropdownFooter={
          <>
            {props.onReset && (
              <Button
                {...(props.subcomponents && props.subcomponents.submitButton)}
                media={<Icon class={iconStyles.common.restore} />}
                render={lang.reset}
                tabIndex={1}
                onClick={props.onReset}
              />
            )}
            <Button
              {...(props.subcomponents && props.subcomponents.resetButton)}
              media={<Icon class={iconStyles.common.reload} />}
              render={lang.apply}
              disabled={!filterForm.isUpdated}
              active={filterForm.isUpdated}
              tabIndex={1}
              data={cleanValues}
              onClick={props.onFilter}
            />
          </>
        }
      />
    </>
  );
}
